import { createContext, Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { AnyActivity } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { useRequiredContext } from '@src/utils/hooks/useRequiredContext';
import { SortBy, SUPPORTED_KINDS } from './activity-feed-constants';
import { ActivityGroup, ActivityId, groupActivities } from './activity-feed-data-utils';
import { ActivityFeedFilterState } from './Filters/activity-feed-filters-constants';
import {
  buildFilterPredicate,
  SetFilterState,
  sortActivities
} from './Filters/activity-feed-filters-utils';
import { useActivityFeedFilters } from './Filters/useActivityFeedFilters';

/**
 * Shared activity-feed data pipeline. Owns filter/sort URL state, in-memory search state, and
 * `activitiesById` — the client-side source of truth for activity contents. Reads from the
 * React Query cache (`rawActivities`) seed and re-sync `activitiesById`; optimistic mutations
 * patch `activitiesById` directly and let the pipeline re-derive.
 *
 * The React Query cache holds server-confirmed state; `activitiesById` holds
 * "what the user is looking at right now", which may include optimistic patches in flight. On
 * successful mutation the two agree; on error, the mutation restores the pre-patch snapshot.
 */

/**
 * User-driven inputs to the pipeline. Changes only on user input, not when a page loads.
 */
export interface ActivityFeedFilterValue {
  filterState: ActivityFeedFilterState;
  setFilterState: SetFilterState;
  sortBy: SortBy | undefined;
  setSortBy: (sortBy: SortBy | undefined) => void;
  /**
   * Sets the match predicate from the list header's search input (defaults to always-true).
   * Because the predicate is itself a function, callers must pass the setter's updater form —
   * `setSearchPredicate(() => newPredicate)` — otherwise React will treat `newPredicate` as a
   * `(prev) => next` reducer and invoke it instead of storing it.
   */
  setSearchPredicate: Dispatch<SetStateAction<(activity: AnyActivity) => boolean>>;
}

export interface ActivityFeedDataValue {
  /** Flat activity IDs in current sort order, filter + search applied. */
  activityIds: ActivityId[];
  /**
   * Client-side source of truth for every activity the pipeline is displaying. Every stage
   * downstream (sort, group, filter, search) reads from here. Server refetches resync
   * the whole registry during render; optimistic mutations patch entries directly. On refetch,
   * in-flight optimistic patches are clobbered by design — server truth wins.
   */
  activitiesById: Record<ActivityId, AnyActivity>;
  /**
   * Mutating counterpart to `activitiesById`. Optimistic mutations call this to apply a patch
   * (`prev => ({ ...prev, [id]: patched })`) or drop an entry (`prev => omitById(prev, id)` for
   * DISMISS). Rollback on error restores the pre-mutation snapshot the same way.
   */
  setActivitiesById: Dispatch<SetStateAction<Record<ActivityId, AnyActivity>>>;
  /** Grouped output for the current sortBy — filter + search applied. Empty buckets omitted. */
  groups: ActivityGroup[];
  /**
   * Flat activity list from the registry, ignoring filter and search. Use when a consumer wants
   * the "searchable universe" independent of active predicates — e.g. the FilterBar's
   * autocomplete source, which shouldn't shrink as the user types.
   */
  activities: AnyActivity[];
  /**
   * Cursor-pagination state, threaded through from the loader hook. `hasNextPage` is true
   * while more pages exist on the server. `isFetchingNextPage` is true from the moment
   * `fetchNextPage` is invoked until the response lands. Consumers (list body) use these to
   * drive the tail-proximity trigger and render a footer spinner.
   */
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const ActivityFeedFilterContext = createContext<ActivityFeedFilterValue | null>(null);
const ActivityFeedDataContext = createContext<ActivityFeedDataValue | null>(null);

/**
 * Read the ActivityFeed filter and sort state and the search-predicate setter.
 * Throws if used outside {@link ActivityFeedDataProvider}.
 */
export const useActivityFeedFilterContext = (): ActivityFeedFilterValue =>
  useRequiredContext(
    ActivityFeedFilterContext,
    'useActivityFeedFilterContext',
    'ActivityFeedDataProvider'
  );

/**
 * Read the ActivityFeed pipeline output and pagination state. Throws if used outside
 * {@link ActivityFeedDataProvider}.
 */
export const useActivityFeedData = (): ActivityFeedDataValue =>
  useRequiredContext(ActivityFeedDataContext, 'useActivityFeedData', 'ActivityFeedDataProvider');

interface ActivityFeedDataProviderProps {
  rawActivities: AnyActivity[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  children: ReactNode;
}

const ALWAYS_TRUE = (): boolean => true;

const buildRegistry = (activities: AnyActivity[]): Record<ActivityId, AnyActivity> => {
  const map: Record<ActivityId, AnyActivity> = {};
  for (const activity of activities) {
    map[activity.id] = activity;
  }
  return map;
};

/**
 * Wire the data pipeline at the page level. Wrap once inside <ActivityFeed>; consumers below
 * read filter state via {@link useActivityFeedFilterContext} and pipeline output via
 * {@link useActivityFeedData}.
 */
export const ActivityFeedDataProvider = ({
  rawActivities,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  children
}: ActivityFeedDataProviderProps): ReactNode => {
  const { filterState, setFilterState, sortBy, setSortBy } = useActivityFeedFilters();
  // Wrap in a lazy initializer so React stores ALWAYS_TRUE itself rather than invoking it —
  // useState treats a bare function argument as a `() => initialState` seed.
  const [searchPredicate, setSearchPredicate] = useState<(activity: AnyActivity) => boolean>(
    () => ALWAYS_TRUE
  );

  // Stage 1: structural pre-filter on the raw fetch: drop kinds the frontend has no card for.
  // (Dismissed activities are excluded server-side, so no dismissed check here.)
  const supportedActivities = useMemo(
    () => rawActivities.filter(activity => SUPPORTED_KINDS.has(activity.kind)),
    [rawActivities]
  );

  // Stage 2: client-side source of truth. Lazy-init from the current server data, then resync on
  // every rawActivities change (refetch, background revalidate, etc.). Optimistic
  // mutations call setActivitiesById directly — those patches survive until the next refetch,
  // which clobbers them by design (server truth wins).
  const [activitiesById, setActivitiesById] = useState<Record<ActivityId, AnyActivity>>(() =>
    buildRegistry(supportedActivities)
  );
  // Resync during render, not in an effect: an effect commits one render where the pagination
  // flags are fresh but the rows are stale, and the list's tail check then fetches an extra page.
  const [syncedActivities, setSyncedActivities] = useState(supportedActivities);
  if (syncedActivities !== supportedActivities) {
    setSyncedActivities(supportedActivities);
    setActivitiesById(buildRegistry(supportedActivities));
  }

  const registryValues = useMemo(() => Object.values(activitiesById), [activitiesById]);

  // Stage 3: pre-compute both sorts on the registry. sortBy toggles downstream become O(1) memo
  // lookups instead of re-running the sort every time. Both re-run when the registry changes,
  // which happens on refetch and on any optimistic mutation.
  const sortedByDate = useMemo(
    () => sortActivities(registryValues, SortBy.MOST_RECENT),
    [registryValues]
  );
  const sortedByImportance = useMemo(
    () => sortActivities(registryValues, SortBy.PRIORITY),
    [registryValues]
  );

  // Stage 4: group each pre-sorted list into buckets. Memoized on their respective sorts.
  const groupedByDate = useMemo(
    () => groupActivities(sortedByDate, SortBy.MOST_RECENT),
    [sortedByDate]
  );
  const groupedByImportance = useMemo(
    () => groupActivities(sortedByImportance, SortBy.PRIORITY),
    [sortedByImportance]
  );

  // Stage 5: pick the grouping for the current sortBy (O(1) reference swap) and apply filter predicates
  // per item in the same memo. Filter-panel clicks + any activitiesById patch re-run this pass
  // so a mutated flag can newly match or stop matching an active filter.
  const filtered = useMemo(() => {
    const source = sortBy === SortBy.PRIORITY ? groupedByImportance : groupedByDate;
    const predicate = buildFilterPredicate(filterState);
    return source.map(group => ({
      ...group,
      items: group.items.filter(id => predicate(activitiesById[id]))
    }));
  }, [sortBy, groupedByDate, groupedByImportance, filterState, activitiesById]);

  // Stage 6: search predicate. Runs on every keystroke — kept as its own stage so typing doesn't
  // invalidate the filter pass.
  const searched = useMemo(
    () =>
      filtered.map(group => ({
        ...group,
        items: group.items.filter(id => searchPredicate(activitiesById[id]))
      })),
    [filtered, searchPredicate, activitiesById]
  );

  // Stage 7: drop empty buckets. Its own memo so the empty-bucket pruning is separated from the search
  // pass — consumers reading `groups` see a stable non-empty list per predicate state.
  const groups = useMemo(() => searched.filter(group => group.items.length > 0), [searched]);

  const activityIds = useMemo(() => groups.flatMap(group => group.items), [groups]);

  const filterValue = useMemo<ActivityFeedFilterValue>(
    () => ({ filterState, setFilterState, sortBy, setSortBy, setSearchPredicate }),
    [filterState, setFilterState, sortBy, setSortBy]
  );

  const dataValue = useMemo<ActivityFeedDataValue>(
    () => ({
      activityIds,
      activitiesById,
      setActivitiesById,
      groups,
      activities: registryValues,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage
    }),
    [
      activityIds,
      activitiesById,
      groups,
      registryValues,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage
    ]
  );

  return (
    <ActivityFeedFilterContext.Provider value={filterValue}>
      <ActivityFeedDataContext.Provider value={dataValue}>
        {children}
      </ActivityFeedDataContext.Provider>
    </ActivityFeedFilterContext.Provider>
  );
};
